/**
 * SotaSearchPanel — Find tab content for SotaFieldPanel.
 *
 * Search type selector at top; inline form below for RAGS/DPDS;
 * launch buttons for EPO CQL (opens OpsRetrievalModal) and EPO Agent
 * (navigates to KSearchPageV3).
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { knowledgeBaseService, type KnowledgeBase } from '../../services/knowledgeBaseService';
import { extractionService, type ExtractionCostEstimate } from '../../services/extractionService';

type SearchType = 'rags' | 'dpds' | 'ops_cql' | 'openalex' | 'ksa';
type Step = 'form' | 'cost' | 'launching';

interface DocsEntry {
  id: string;
  label: string;
  transcriptId: string;
}

interface Props {
  subProjectId: string;
  onOpenOpsModal: () => void;
  onOpenOpenalexModal: () => void;
  onNavigateToKSearch: () => void;
  onSearchLaunched: (entry: DocsEntry) => void;
}

const REGIME: Record<'rags' | 'dpds', 'A' | 'B'> = { rags: 'B', dpds: 'A' };

const SotaSearchPanel: React.FC<Props> = ({
  subProjectId,
  onOpenOpsModal,
  onOpenOpenalexModal,
  onNavigateToKSearch,
  onSearchLaunched,
}) => {
  const { t } = useTranslation();
  const [searchType, setSearchType] = useState<SearchType | ''>('');

  // Extraction form state (RAGS / DPDS)
  const [step, setStep] = useState<Step>('form');
  const [kbs, setKbs] = useState<KnowledgeBase[]>([]);
  const [kbsLoading, setKbsLoading] = useState(false);
  const [sourceKbId, setSourceKbId] = useState('');
  const [intent, setIntent] = useState('');
  const [keywords, setKeywords] = useState('');
  const [destKbId, setDestKbId] = useState('');
  const [costEstimate, setCostEstimate] = useState<ExtractionCostEstimate | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load KBs when an extraction type is selected
  useEffect(() => {
    if (searchType !== 'rags' && searchType !== 'dpds') return;
    if (kbs.length > 0) return;
    setKbsLoading(true);
    knowledgeBaseService.getAll()
      .then((all) => {
        setKbs(all);
        if (all.length === 1) setSourceKbId(all[0].id);
      })
      .catch(() => {})
      .finally(() => setKbsLoading(false));
  }, [searchType, kbs.length]);

  const resetForm = () => {
    setStep('form');
    setSourceKbId('');
    setIntent('');
    setKeywords('');
    setDestKbId('');
    setCostEstimate(null);
    setError(null);
  };

  const handleTypeChange = (type: SearchType | '') => {
    setSearchType(type);
    resetForm();
  };

  const handleEstimate = async () => {
    if (!sourceKbId || !intent.trim() || (searchType !== 'rags' && searchType !== 'dpds')) return;
    setEstimating(true);
    setError(null);
    try {
      const est = await extractionService.estimateCost(subProjectId, sourceKbId, REGIME[searchType]);
      setCostEstimate(est);
      setStep('cost');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('sota.search.error.estimate', 'Failed to estimate cost.'));
    } finally {
      setEstimating(false);
    }
  };

  const handleLaunch = async () => {
    if (!sourceKbId || !intent.trim() || (searchType !== 'rags' && searchType !== 'dpds')) return;
    setLaunching(true);
    setStep('launching');
    setError(null);
    try {
      const kwList = keywords.split(',').map(k => k.trim()).filter(Boolean);
      const res = await extractionService.launch(subProjectId, {
        kb_id: sourceKbId,
        intent: intent.trim(),
        target_context: 'state_of_the_art',
        destination_kb_id: destKbId || undefined,
        must_have_keywords: kwList,
        forced_regime: REGIME[searchType],
      });
      const label = searchType === 'rags' ? 'RAGS' : 'DPDS';
      const entry: DocsEntry = {
        id: res.transcript_id,
        label: `${label} — ${intent.trim().substring(0, 30)}${intent.length > 30 ? '…' : ''}`,
        transcriptId: res.transcript_id,
      };
      onSearchLaunched(entry);
      window.open(`/search-transcript/${res.transcript_id}`, '_blank');
      resetForm();
      setSearchType('');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('sota.search.error.launch', 'Failed to launch search.'));
      setStep('cost');
    } finally {
      setLaunching(false);
    }
  };

  const formatCost = (cents: number, currency: string) =>
    `${currency === 'EUR' ? '€' : '$'}${(cents / 100).toFixed(2)}`;

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 11, fontWeight: 600,
    color: '#374151', marginBottom: 3,
  };
  const inputStyle: React.CSSProperties = {
    width: '100%', fontSize: 12, padding: '4px 8px',
    border: '1px solid #d1d5db', borderRadius: 4,
    boxSizing: 'border-box',
  };
  const btnPrimary: React.CSSProperties = {
    fontSize: 11, padding: '3px 12px', borderRadius: 3,
    background: '#2563eb', color: '#fff', border: 'none',
    fontWeight: 600, cursor: 'pointer',
  };
  const btnSecondary: React.CSSProperties = {
    fontSize: 11, padding: '3px 12px', borderRadius: 3,
    background: '#f3f4f6', color: '#374151',
    border: '1px solid #d1d5db', cursor: 'pointer',
  };

  return (
    <div style={{ padding: '8px 12px 6px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Search type selector */}
      <div>
        <label style={labelStyle}>{t('sota.search.selectType', 'Search type')}</label>
        <select
          style={{ ...inputStyle, color: searchType ? '#111827' : '#9ca3af' }}
          value={searchType}
          onChange={e => handleTypeChange(e.target.value as SearchType | '')}
        >
          <option value="" disabled>{t('sota.search.placeholder', 'Select a search method…')}</option>
          <option value="rags">{t('sota.search.rags.label', 'RAGS — Deep KB Retrieval')}</option>
          <option value="dpds">{t('sota.search.dpds.label', 'DPDS — Document-per-Document Search')}</option>
          <option value="ops_cql">{t('sota.search.ops.label', 'EPO CQL — Direct Patent Query')}</option>
          <option value="openalex">{t('sota.search.openalex.label', 'OpenAlex — Direct Literature Query')}</option>
          {/* KSA agent PARKED 2026-09-01 (founder decision): broken/half-baked, greyed out
              until a future eval-first rebuild. Do not re-enable without a fix. */}
          <option value="ksa" disabled>
            {t('sota.search.ksa.label', 'EPO Agent — Guided Keyword Search')} — {t('sota.search.ksa.unavailable', 'Temporarily unavailable')}
          </option>
        </select>
      </div>

      {/* RAGS / DPDS inline form */}
      {(searchType === 'rags' || searchType === 'dpds') && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* Regime banner */}
          <div style={{
            fontSize: 11, padding: '4px 8px', borderRadius: 4,
            background: searchType === 'rags' ? '#f5f3ff' : '#eff6ff',
            color: searchType === 'rags' ? '#6d28d9' : '#1d4ed8',
            border: `1px solid ${searchType === 'rags' ? '#c4b5fd' : '#93c5fd'}`,
          }}>
            {searchType === 'rags'
              ? t('extraction.regimeB.description', 'Documents ranked using combined keyword and semantic similarity search, then top results individually assessed.')
              : t('extraction.regimeA.description', 'Every document in this KB will be individually assessed by AI for relevance. Max 5000 documents.')}
          </div>

          {step === 'form' && (
            <>
              {/* Source KB */}
              <div>
                <label style={labelStyle}>{t('extraction.sourceKb', 'Source Knowledge Base')} <span style={{ color: '#ef4444' }}>*</span></label>
                {kbsLoading ? (
                  <p style={{ fontSize: 11, color: '#9ca3af' }}>{t('common.loading', 'Loading...')}</p>
                ) : (
                  <select style={inputStyle} value={sourceKbId} onChange={e => { setSourceKbId(e.target.value); setCostEstimate(null); }}>
                    <option value="" disabled>{t('extraction.selectKbPlaceholder', 'Select a Knowledge Base…')}</option>
                    {kbs.map(kb => (
                      <option key={kb.id} value={kb.id}>{kb.name} ({kb.document_count ?? 0} docs)</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Intent */}
              <div>
                <label style={labelStyle}>{t('extraction.intent', 'Intent')} <span style={{ color: '#ef4444' }}>*</span></label>
                <textarea
                  style={{ ...inputStyle, resize: 'vertical' }}
                  rows={3}
                  placeholder={t('extraction.intentPlaceholder', 'Describe what you are looking for in 1–3 sentences…')}
                  value={intent}
                  onChange={e => setIntent(e.target.value)}
                  maxLength={1000}
                />
              </div>

              {/* Keywords */}
              <div>
                <label style={labelStyle}>
                  {t('extraction.keywords', 'Must-have keywords')}
                  <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 4 }}>{t('extraction.optional', '(optional, comma-separated)')}</span>
                </label>
                <input
                  type="text"
                  style={inputStyle}
                  placeholder={t('extraction.keywordsPlaceholder', 'e.g. thermal, heat exchanger')}
                  value={keywords}
                  onChange={e => setKeywords(e.target.value)}
                />
              </div>

              {/* Destination KB */}
              <div>
                <label style={labelStyle}>{t('extraction.destinationKb', 'Destination Knowledge Base')}</label>
                <select style={inputStyle} value={destKbId} onChange={e => setDestKbId(e.target.value)}>
                  <option value="">{t('extraction.destinationKbNone', '— None (review results only)')}</option>
                  {kbs.filter(kb => kb.id !== sourceKbId).map(kb => (
                    <option key={kb.id} value={kb.id}>{kb.name}</option>
                  ))}
                </select>
              </div>

              {error && <p style={{ fontSize: 11, color: '#dc2626' }}>{error}</p>}

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  style={{ ...btnPrimary, opacity: (!sourceKbId || !intent.trim() || estimating) ? 0.5 : 1 }}
                  disabled={!sourceKbId || !intent.trim() || estimating}
                  onClick={handleEstimate}
                >
                  {estimating ? t('extraction.estimating', 'Estimating…') : t('extraction.nextEstimate', 'Estimate Cost')}
                </button>
              </div>
            </>
          )}

          {step === 'cost' && costEstimate && (
            <>
              <div style={{
                background: '#f9fafb', border: '1px solid #e5e7eb',
                borderRadius: 4, padding: '8px 10px', fontSize: 11,
                display: 'flex', flexDirection: 'column', gap: 3,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{t('extraction.costDocs', 'Documents in KB')}</span>
                  <span>{costEstimate.total_documents.toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{t('extraction.costLlmCalls', 'Estimated LLM calls')}</span>
                  <span>{costEstimate.estimated_llm_calls.toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, borderTop: '1px solid #e5e7eb', paddingTop: 4, marginTop: 2 }}>
                  <span>{t('extraction.costEstimated', 'Estimated cost')}</span>
                  <span>{formatCost(costEstimate.estimated_cost_cents, costEstimate.currency)}</span>
                </div>
              </div>
              {error && <p style={{ fontSize: 11, color: '#dc2626' }}>{error}</p>}
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                <button style={btnSecondary} onClick={() => setStep('form')}>{t('common.back', 'Back')}</button>
                <button style={btnPrimary} disabled={launching} onClick={handleLaunch}>
                  {launching ? t('extraction.launching', 'Launching…') : t('extraction.confirmLaunch', 'Confirm & Launch')}
                </button>
              </div>
            </>
          )}

          {step === 'launching' && (
            <p style={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic' }}>
              {t('extraction.launching', 'Launching extraction — results will open in a new tab…')}
            </p>
          )}
        </div>
      )}

      {/* EPO CQL */}
      {searchType === 'ops_cql' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p style={{ fontSize: 11, color: '#374151' }}>
            {t('sota.search.ops.description', 'Search the EPO Espacenet database using a structured CQL query. Supports keywords, CPC codes, dates, and applicant/inventor filters.')}
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button style={btnPrimary} onClick={onOpenOpsModal}>
              {t('sota.search.ops.launch', 'Open EPO CQL Search →')}
            </button>
          </div>
        </div>
      )}

      {/* OpenAlex */}
      {searchType === 'openalex' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p style={{ fontSize: 11, color: '#374151' }}>
            {t('sota.search.openalex.description', 'Search the OpenAlex scientific-literature database. Suggests relevant OpenAlex topics, and supports keyword and author filters. Saves metadata + abstracts to a knowledge base.')}
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button style={btnPrimary} onClick={onOpenOpenalexModal}>
              {t('sota.search.openalex.launch', 'Open OpenAlex Search →')}
            </button>
          </div>
        </div>
      )}

      {/* EPO Agent / KSA */}
      {searchType === 'ksa' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p style={{ fontSize: 11, color: '#374151' }}>
            {t('sota.search.ksa.description', 'Let the AI agent iteratively refine keyword queries against EPO Espacenet based on your claim chart features.')}
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button style={btnPrimary} onClick={onNavigateToKSearch}>
              {t('sota.search.ksa.launch', 'Go to KSearch →')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SotaSearchPanel;
